// Who the account menu says is signed in (pure, so it is unit-testable without rendering).
// - The member's own `users` item first, as iOS MoreMenuProfileHeader shows it: `User.fullName` ("first last",
//   trimmed) on `Theme.color(hex: colorHex)`.
// - Until that row has synced (or when it has no name yet), the auth account: provider metadata from Google/Apple,
//   else the email's local part.

import type { User } from '@supabase/supabase-js';
import { decodeUser } from '@/contracts/models/users';
import type { CachedRow } from '@/sync/replica';

export interface ProfileLook {
  /** `User.fullName`; empty for a profile whose names are both blank. */
  name: string;
  colorHex: string;
}

/** The signed-in member's profile as the iPhone draws it; null when the row is undecodable (a newer app's item). */
export function profileLook(row: CachedRow): ProfileLook | null {
  const decoded = decodeUser(row.payload, row.id);
  if (decoded.undecodable) return null;
  const { firstName, lastName, colorHex } = decoded.value;
  return { name: `${firstName} ${lastName}`.trim(), colorHex };
}

/** A display name from the auth account: provider metadata (Google/Apple), else the email's local part. */
export function accountDisplayName(user: User | null, email: string | null): string {
  const meta = (user?.user_metadata ?? {}) as Record<string, unknown>;
  const pick = (key: string) => {
    const value = meta[key];
    return typeof value === 'string' ? value.trim() : '';
  };
  const full = pick('full_name') || pick('name');
  if (full) return full;
  const joined = [pick('first_name') || pick('given_name'), pick('last_name') || pick('family_name')]
    .filter(Boolean)
    .join(' ');
  if (joined) return joined;
  const address = email ?? user?.email ?? user?.phone ?? '';
  return address.split('@')[0] || 'Account';
}
